import type { FormsApiClient } from '../api';
import type { FieldDefinition, LeadFormPublicConfig } from '../types';
import { readFieldValues, renderField } from './formFields';
import { buildCountdown } from './countdown';
import { buildRecommendations } from './recommendations';
import { resolvePopupStyle, type LeadFormTheme, type ResolvedPopupStyle } from './templates';

/** Building blocks every panel-based renderer (lightbox, ribbon, corner card, screen filler,
 * two-step, widget, coupon wheel) shares — extracted out of what used to be lightbox-only code so
 * adding a renderer means composing these, not re-implementing panel chrome from scratch. Renderers
 * that don't use an overlay panel at all (in-content, recommendations) don't need this module.
 */

/** Rules every renderer's own stylesheet can rely on existing — positioning (overlay vs. bar vs.
 * corner card) is each renderer's own concern, layered in before/after this. */
export const SHARED_PANEL_STYLE = `
  :host { all: initial; }
  * { box-sizing: border-box; font-family: system-ui, -apple-system, sans-serif; }
  .panel {
    background: var(--cms-lf-bg); color: var(--cms-lf-text);
    border: var(--cms-lf-border); border-radius: var(--cms-lf-panel-radius);
    padding: 24px; box-shadow: 0 20px 60px rgba(0,0,0,0.3); position: relative;
  }
  .image { display: block; width: 100%; max-height: 160px; object-fit: cover;
    border-radius: var(--cms-lf-panel-radius); margin-bottom: 16px; }
  .headline { margin: 0 0 8px; font-size: 20px; font-weight: 600; line-height: 1.3; }
  .body-copy { margin: 0 0 16px; font-size: 14px; line-height: 1.5; opacity: 0.85; }
  .close { position: absolute; top: 8px; right: 8px; border: none; background: transparent;
    font-size: 20px; cursor: pointer; line-height: 1; color: currentColor; opacity: 0.6; }
  .cms-lf-field { display: block; margin-bottom: 12px; font-size: 14px; }
  .cms-lf-field input, .cms-lf-field select, .cms-lf-field textarea {
    display: block; width: 100%; margin-top: 4px; padding: 8px; box-sizing: border-box;
    border: 1px solid #ccc; border-radius: 4px; font-size: 14px; color: #111;
  }
  .submit { width: 100%; padding: 10px; border: none; border-radius: var(--cms-lf-button-radius);
    background: var(--cms-lf-button-bg); color: var(--cms-lf-button-text); font-size: 14px;
    font-weight: 600; cursor: pointer; margin-top: 8px; }
  .submit:disabled { opacity: 0.6; cursor: default; }
  .countdown { display: flex; align-items: baseline; gap: 6px; margin: 0 0 16px; font-size: 14px; }
  .countdown-label { opacity: 0.75; }
  .countdown-clock { font-variant-numeric: tabular-nums; font-weight: 700; font-size: 18px; }
  .recommendations { display: flex; gap: 10px; overflow-x: auto; margin: 0 0 16px; padding-bottom: 2px; }
  .rec-card {
    flex: 0 0 auto; width: 110px; display: flex; flex-direction: column; gap: 4px;
    text-decoration: none; color: inherit; font-size: 12px;
  }
  .rec-card img { width: 100%; height: 80px; object-fit: cover; border-radius: 6px; }
  .rec-title { font-weight: 600; line-height: 1.3; }
  .rec-price { font-weight: 700; }
  .rec-compare { margin-left: 6px; font-weight: 400; text-decoration: line-through; opacity: 0.6; }
`;

/** Creates the shadow-DOM host every renderer mounts into (isolating the widget's styles from the
 * host page's cascade, same reasoning the original lightbox.ts documented) and injects `css` into
 * its root. `mountPoint` defaults to `document.body`; IN_CONTENT passes a placeholder element
 * instead. */
export function createHost(css: string, mountPoint: Element = document.body): { host: HTMLElement; root: ShadowRoot } {
  const host = document.createElement('div');
  mountPoint.appendChild(host);
  const root = host.attachShadow({ mode: 'open' });

  const styleEl = document.createElement('style');
  styleEl.textContent = css;
  root.appendChild(styleEl);

  return { host, root };
}

export function applyThemeVars(el: HTMLElement, resolved: ResolvedPopupStyle): void {
  el.style.setProperty('--cms-lf-bg', resolved.background);
  el.style.setProperty('--cms-lf-text', resolved.text);
  el.style.setProperty('--cms-lf-border', resolved.border);
  el.style.setProperty('--cms-lf-panel-radius', resolved.panelRadius);
  el.style.setProperty('--cms-lf-button-radius', resolved.buttonRadius);
  el.style.setProperty('--cms-lf-button-bg', resolved.buttonBg);
  el.style.setProperty('--cms-lf-button-text', resolved.buttonText);
}

export function buildCloseButton(onClose: () => void): HTMLButtonElement {
  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'close';
  closeButton.setAttribute('aria-label', 'Close');
  closeButton.textContent = '×';
  closeButton.addEventListener('click', onClose);
  return closeButton;
}

/** Image/headline/body/countdown/recommendations, in that order — the copy chrome every panel
 * stacks above its form. `panelRoot` is passed straight through to the countdown for its expiry
 * behavior; `client` is only used (to fetch product cards) when `theme.recommendations` is set. */
export function buildHeader(form: LeadFormPublicConfig, client: FormsApiClient, panelRoot: HTMLElement): HTMLElement[] {
  const theme = form.theme as LeadFormTheme;
  const elements: HTMLElement[] = [];

  if (theme.imageUrl) {
    const img = document.createElement('img');
    img.className = 'image';
    img.src = theme.imageUrl;
    img.alt = '';
    elements.push(img);
  }

  if (theme.headline) {
    const headline = document.createElement('p');
    headline.className = 'headline';
    headline.textContent = theme.headline;
    elements.push(headline);
  }

  if (theme.body) {
    const body = document.createElement('p');
    body.className = 'body-copy';
    body.textContent = theme.body;
    elements.push(body);
  }

  const countdown = buildCountdown(theme.countdown, form.id, panelRoot);
  if (countdown) elements.push(countdown);

  if (theme.recommendations) {
    elements.push(buildRecommendations(form, client));
  }

  return elements;
}

/** The lead-capture `<form>`, wired with the disable-on-submit / re-enable-on-failure behavior
 * every renderer needs identically. */
export function buildForm(
  fields: FieldDefinition[],
  submitLabel: string,
  onSubmit: (data: Record<string, unknown>) => Promise<boolean>,
  onSuccess: () => void,
): HTMLFormElement {
  const formEl = document.createElement('form');
  for (const field of fields) {
    formEl.appendChild(renderField(field));
  }

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'submit';
  submitButton.textContent = submitLabel;
  formEl.appendChild(submitButton);

  formEl.addEventListener('submit', async (e) => {
    e.preventDefault();
    submitButton.disabled = true;
    const values = readFieldValues(formEl, fields);
    const ok = await onSubmit(values);
    if (ok) {
      onSuccess();
    } else {
      submitButton.disabled = false;
    }
  });

  return formEl;
}

export { resolvePopupStyle };
export type { LeadFormTheme, ResolvedPopupStyle };

/** What every renderer (render/index.ts's dispatch table) hands back to index.ts, regardless of
 * display type. */
export interface RenderHandle {
  close: () => void;
}
